import { makeAutoObservable, reaction, runInAction, toJS } from "mobx";
import { nanoid } from "nanoid";
import * as Y from "yjs";

import { createMessage, createReaction, deleteReaction } from "@/api";
import { DocumentModel } from "@/models/DocumentModel";
import { nodeFromJson, isNodeJson } from "@/models/nodes/nodeFromJson";
import { base64toUint8Array } from "@/utils/base64/base64toUint8Array";

import type { ChatModel } from "./ChatModel";
import type { Message, UserShort, Reaction, Upload } from "@/api";
import type { TextNodeModel } from "@/models/nodes/TextNodeModel";
import type { NodeJson } from "@corca/data";
import type { CollectedMention } from "@corca/tiptap";

export type MessageLocalStatus = "sending" | "failed";

const CREATE_MESSAGE_TIMEOUT = 10_000;
// Keep in sync with apps/chat-bot/src/openai/sendReplyMessage.ts
const GENERATION_ERROR_META_KEY = "generationError";
export const STREAM_STATE_META_KEY = "streamState";
export const STREAMING_STATE = "streaming";

export class ChatMessageModel {
  messageData: Message;

  doc: DocumentModel | undefined;

  chat: ChatModel;

  readonly localId = nanoid();

  private bodyReactionDispose: () => void;

  localStatus?: MessageLocalStatus;

  constructor(messageData: Message, chat: ChatModel, localStatus?: MessageLocalStatus) {
    this.chat = chat;
    this.messageData = messageData;
    this.localStatus = localStatus;

    // makeAutoObservable must be called before reaction so that `this.body`
    // is already a computed — otherwise MobX tracks zero dependencies and
    // the reaction never fires on subsequent body changes.
    makeAutoObservable(this);

    this.bodyReactionDispose = reaction(
      () => this.body,
      () => this.updateDoc(),
    );
    this.createDoc();
  }

  static async create(
    body: string,
    mentions: CollectedMention[],
    chat: ChatModel,
    fileKeys?: string[],
    optimisticFiles?: Upload[],
    onOptimisticSuccess?: (
      data: { message?: Message },
      optimisticData: { message?: Message },
    ) => void,
    meta?: Record<string, string>,
  ): Promise<ChatMessageModel | undefined> {
    const clientMessageUid = `local-${nanoid()}`;
    const optimisticMessageData = {
      message: {
        uid: clientMessageUid,
        body,
        meta: meta ?? {},
        user: {
          typename: "UserShort",
          name: chat.userData?.name ?? "",
          username: chat.userData?.username ?? "",
          avatarUrls: chat.userData?.avatarUrls,
        },
        createdAt: new Date().toISOString(),
        cursor: new Date().toISOString().replace("Z", "000Z"),
        files: optimisticFiles,
        typename: "Message",
      } as Message,
    };

    let newMessage: ChatMessageModel | undefined;

    const { message } = await createMessage(
      {
        uid: chat.uid,
        body,
        clientMessageUid,
        fileKeys,
        meta: { currentDocumentUid: chat.docId, ...meta },
        mentions: mentions.length > 0 ? mentions : undefined,
      },
      {
        timeout: CREATE_MESSAGE_TIMEOUT,
        optimistic: {
          data: optimisticMessageData,
          onError: () => {
            runInAction(() => {
              if (newMessage) {
                newMessage.localStatus = "failed";
              }
            });
          },
          onSuccess: (data) => {
            onOptimisticSuccess?.(data, optimisticMessageData);
            runInAction(() => {
              if (newMessage) {
                newMessage.localStatus = undefined;
              }
            });
          },
        },
      },
    );

    if (message) {
      newMessage = new ChatMessageModel(message, chat, "sending");
      return newMessage;
    }
  }

  setMessageData(messageData: Message): void {
    this.revokeOptimisticFileUrls();
    this.messageData = messageData;
  }

  private revokeOptimisticFileUrls(): void {
    this.messageData.files?.forEach((file) => {
      if (file.signedUrl.startsWith("blob:")) {
        URL.revokeObjectURL(file.signedUrl);
      }
    });
  }

  dispose(): void {
    this.revokeOptimisticFileUrls();
    this.bodyReactionDispose();
  }

  get body(): string | null | undefined {
    return this.messageData.body;
  }

  get mentions(): UserShort[] {
    return this.messageData.mentions ?? [];
  }

  get hasGenerationError(): boolean {
    return this.messageData.meta[GENERATION_ERROR_META_KEY] === "true";
  }

  /** A bot reply still being written */
  get isStreaming(): boolean {
    return this.messageData.meta[STREAM_STATE_META_KEY] === STREAMING_STATE;
  }

  get uid(): string {
    return this.messageData.uid;
  }

  get user(): UserShort {
    return this.messageData.user;
  }

  get cursor(): string {
    return this.messageData.cursor;
  }

  get createdAt(): string {
    return this.messageData.createdAt;
  }

  get files(): Upload[] {
    return this.messageData.files ?? [];
  }

  get reactions(): Reaction[] {
    if (!this.messageData.reactions) {
      this.messageData.reactions = [];
    }
    return this.messageData.reactions;
  }

  createReaction(emoji: string) {
    createReaction(this.uid, { emoji });
  }

  deleteReaction(emoji: string) {
    deleteReaction(this.uid, { emoji });
  }

  /** Try to resend failed local message from local storage */
  resend() {
    if (this.localStatus === "failed" && this.body) {
      this.chat.resendLocalMessage(this.uid);
    }
  }

  /** Remove failed local message from local storage */
  delete() {
    if (this.localStatus === "failed") {
      this.chat.removeLocalMessage(this.uid, true);
    }
  }

  private createDoc(): void {
    const { body } = this;

    if (!body) {
      this.doc = undefined;
      return;
    }

    this.doc = this.createDocFromJson(body) || this.createDocFromBase64(body);
  }

  private updateDoc(): void {
    const { body } = this;

    if (!body) {
      if (this.doc) {
        this.doc = undefined;
      }
      return;
    }

    if (this.updateDocFromBase64(body)) {
      return;
    }

    // Fallback: recreate doc from scratch (handles JSON bodies from deployed bot)
    this.doc = this.createDocFromJson(body) || this.createDocFromBase64(body);
  }

  private updateDocFromBase64(base64Str: string): boolean {
    if (!this.doc?.yDoc) {
      return false;
    }

    try {
      Y.applyUpdate(this.doc.yDoc, base64toUint8Array(base64Str));
      return true;
    } catch (_err) {
      // not a base64 string
      return false;
    }
  }

  private createDocFromBase64(base64Str: string): DocumentModel | undefined {
    try {
      const newYDoc = new Y.Doc();
      Y.applyUpdate(newYDoc, base64toUint8Array(base64Str));
      const doc = new DocumentModel(this.uid, newYDoc);
      doc.disableEditing = true;
      return doc;
    } catch (_err) {
      // not a base64 string
    }
  }

  private createDocFromJson(jsonStr: string): DocumentModel | undefined {
    const docJson = this.parseJson(jsonStr);

    if (docJson) {
      const doc = new DocumentModel();
      doc.transact(
        () => {
          doc.disableEditing = true;
          const textNode = nodeFromJson(doc, docJson) as TextNodeModel;
          if (textNode) {
            doc.textNode = textNode;
          }
        },
        "ChatMessageModel:createDocFromJson",
        { runInAction: true },
      );
      return doc;
    }
  }

  private parseJson(jsonStr: string): NodeJson | undefined {
    try {
      const result = JSON.parse(jsonStr);
      if (isNodeJson(result, { ignoreId: true })) {
        return result;
      }
      return undefined;
    } catch (_e) {
      return undefined;
    }
  }

  toJSON() {
    return toJS(this.messageData);
  }

  applyReaction(reaction: Reaction): void {
    const { reactions } = this;
    const { emoji, count, users } = reaction;
    const currentReactionIdx = reactions.findIndex((r) => r.emoji === emoji);
    if (currentReactionIdx !== -1) {
      reactions[currentReactionIdx].count = count;
      reactions[currentReactionIdx].users = users;
    } else {
      reactions.push(reaction);
    }
  }
}
